import React from "react";
import { Heart, TrendingUp, Users, ScanLine } from "lucide-react";

/**
 * Gym Health Score - frontend-computed from existing dashboard stats.
 * Composite of 4 sub-scores: lead response, trial conversion, retention, attendance.
 * Pure math, zero new backend calls.
 */
function computeHealth(stats = {}) {
  const leads = stats.total_leads_month ?? 0;
  const trials = stats.trials_booked ?? 0;
  const conversions = stats.conversions_month ?? 0;
  const active = stats.active_members ?? 0;
  const atRisk = stats.at_risk ?? 0;
  const checkinsToday = stats.checkins_today ?? 0;

  // Lead Response - how many leads were moved out of "new" into trial. If
  // there are zero leads we default to a neutral 70 (no signal, not bad).
  const leadResponse = leads === 0 ? 70 : Math.min(100, Math.round((trials / leads) * 130));

  // Trial Conversion - % of trials that converted to members.
  const trialConversion = trials === 0 ? 70 : Math.min(100, Math.round((conversions / trials) * 100));

  // Retention - 100 minus the share of active members who are at risk.
  const retention = active === 0 ? 70 : Math.max(0, 100 - Math.round((atRisk / active) * 100));

  // Attendance - share of active members who checked in today. Cap at 100.
  const attendance = active === 0 ? 70 : Math.min(100, Math.round((checkinsToday / active) * 150));

  const overall = Math.round((leadResponse + trialConversion + retention + attendance) / 4);

  return {
    overall,
    label: overall >= 80 ? "Healthy" : overall >= 60 ? "Steady" : overall >= 40 ? "Needs attention" : "At risk",
    subs: [
      { key: "lead_response",    label: "Lead Response",    value: leadResponse,    Icon: TrendingUp, tone: "leads"   },
      { key: "trial_conversion", label: "Trial Conversion", value: trialConversion, Icon: Users,      tone: "trials"  },
      { key: "retention",        label: "Retention",        value: retention,       Icon: Heart,      tone: "growth"  },
      { key: "attendance",       label: "Attendance",       value: attendance,      Icon: ScanLine,   tone: "ops"     },
    ],
  };
}

const TONE_BAR = {
  growth: "bg-growth",
  leads: "bg-leads",
  trials: "bg-trials",
  tasks: "bg-tasks",
  ai: "bg-ai",
  risk: "bg-risk",
  ops: "bg-ops",
};
const TONE_ICON = {
  growth: "text-growth bg-growth/10",
  leads:  "text-leads  bg-leads/10",
  trials: "text-trials bg-trials/10",
  tasks:  "text-tasks  bg-tasks/10",
  ai:     "text-ai     bg-ai/10",
  risk:   "text-risk   bg-risk/10",
  ops:    "text-muted-foreground bg-muted",
};

export default function GymHealthCard({ stats, loading }) {
  const health = computeHealth(stats || {});

  return (
    <section
      data-testid="gym-health-card"
      className="rounded-2xl border border-border bg-card p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70 mb-1.5">
            Gym Health
          </p>
          <h3 className="font-display text-lg font-bold text-foreground tracking-tight">
            How your gym is doing
          </h3>
        </div>
        <div className="text-right">
          <div className="font-display text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-none" data-testid="gym-health-score">
            {loading ? "-" : health.overall}
            <span className="text-base text-muted-foreground font-medium ml-1">/ 100</span>
          </div>
          <p
            className={`text-xs font-semibold mt-1.5 ${
              health.overall >= 80 ? "text-growth" :
              health.overall >= 60 ? "text-leads" :
              health.overall >= 40 ? "text-trials" : "text-risk"
            }`}
            data-testid="gym-health-label"
          >
            {loading ? "-" : health.label}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {health.subs.map(s => (
          <div key={s.key} data-testid={`health-sub-${s.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-7 h-7 rounded-md flex items-center justify-center ${TONE_ICON[s.tone]}`}>
                <s.Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
              </span>
              <span className="text-xs font-medium text-foreground/80">{s.label}</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${TONE_BAR[s.tone]}`}
                style={{ width: `${loading ? 0 : s.value}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">{loading ? "-" : `${s.value}`}<span className="text-muted-foreground/60">/100</span></p>
          </div>
        ))}
      </div>
    </section>
  );
}
